import React, { useEffect, useState } from 'react';
import axios from 'axios';
import BRAND, { applyBrandTheme } from '../brandConfig';
import { fmtMoney, fmtDate } from '../lib/format';

// The public pay page: qpshub.com/pay/<token>.
//
// Opened from a text, an email, a printed invoice's QR code or the Pay button
// in the portal. No login: the link is the permission, and a customer who has
// to find a password first pays next week instead of now.
//
// It shows what they owe and hands them to Stripe's own checkout page to pay.
// Whether a payment went through is decided by Stripe telling the server, not
// by this page — coming back with ?paid=1 only changes what it says.
export default function PayScreen({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const params = new URLSearchParams(window.location.search);
  const justPaid = params.get('paid') === '1';
  const canceled = params.get('canceled') === '1';

  useEffect(() => { applyBrandTheme(); }, []);

  useEffect(() => {
    axios.get(`/api/pay/${encodeURIComponent(token)}`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.message || 'This pay link could not be opened.'));
  }, [token]);

  const pay = async (lineId) => {
    setBusy(lineId || 'all');
    try {
      const res = await axios.post(`/api/pay/${encodeURIComponent(token)}/checkout`, lineId ? { lineId } : {});
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.message || 'Payment could not be started. Please try again.');
      setBusy(null);
    }
  };

  const open = (data?.items || []).filter(i => i.owed > 0.004);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '24px 16px 48px' }}>
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 700, color: 'var(--navy)' }}>
            {data?.businessName || BRAND.name.full}
          </div>
          {data && <div style={{ color: 'var(--muted)', marginTop: 4 }}>{data.title}{data.address ? ` · ${data.address}` : ''}</div>}
        </div>

        {justPaid && (
          <div className="card" style={{ background: 'var(--green-light)', borderLeft: '4px solid var(--green)', marginBottom: 16 }}>
            <strong>Thank you, your payment is on its way.</strong>
            <div style={{ marginTop: 6, color: 'var(--slate)' }}>
              A card payment shows as paid within a minute. A bank payment takes 3 to 5 business days to clear,
              and shows as Processing until then. You don't need to do anything else.
            </div>
          </div>
        )}
        {canceled && !justPaid && (
          <div className="card" style={{ marginBottom: 16 }}>No payment was taken. You can pay whenever you're ready.</div>
        )}

        {error && <div className="card" style={{ background: 'var(--red-light)', color: 'var(--red)', marginBottom: 16 }}>{error}</div>}

        {!data && !error && <div className="card" style={{ textAlign: 'center', color: 'var(--muted)' }}>Loading…</div>}

        {data && (
          <>
            <div className="card" style={{ marginBottom: 16, textAlign: 'center' }}>
              <div style={{ color: 'var(--muted)', fontSize: 13, textTransform: 'uppercase', letterSpacing: '.06em' }}>Balance due</div>
              <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--navy)', margin: '6px 0' }}>{fmtMoney(data.balance)}</div>
              {data.pending > 0 && (
                <div style={{ color: 'var(--amber)', fontSize: 13 }}>{fmtMoney(data.pending)} processing (bank payment clearing)</div>
              )}
              {data.balance > 0.004 && data.payable && (
                <button className="btn btn-primary btn-lg" style={{ width: '100%', marginTop: 14 }} disabled={!!busy} onClick={() => pay(null)}>
                  {busy === 'all' ? 'Opening secure checkout…' : `Pay ${fmtMoney(data.balance)}`}
                </button>
              )}
              {data.balance > 0.004 && !data.payable && (
                <div style={{ marginTop: 12, color: 'var(--slate)' }}>
                  Online payment isn't available for this invoice yet. Please contact {data.businessName} to pay.
                </div>
              )}
              {data.balance <= 0.004 && data.pending <= 0 && (
                <div style={{ marginTop: 8, color: 'var(--green)', fontWeight: 700 }}>Paid in full. Thank you!</div>
              )}
            </div>

            {data.payable && data.balance > 0.004 && (
              <div className="card" style={{ marginBottom: 16, fontSize: 13, color: 'var(--slate)' }}>
                <strong style={{ color: 'var(--navy)' }}>Pay by bank transfer or card.</strong> Bank transfer (ACH) is
                the first option on the next page: you sign in to your bank and it's done. Cards and Apple Pay /
                Google Pay work too. Payments are processed securely by Stripe.
              </div>
            )}

            {data.kind === 'project' && open.length > 1 && data.payable && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Or pay one invoice at a time</div>
                {open.map(i => (
                  <div key={i.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 0', borderTop: '1px solid var(--border)' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{i.label}</div>
                      <div style={{ color: 'var(--muted)', fontSize: 12 }}>{i.date ? fmtDate(i.date) : ''}</div>
                    </div>
                    <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={() => pay(i.id)}>
                      {busy === i.id ? 'Opening…' : `Pay ${fmtMoney(i.owed)}`}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {(data.lineItems || []).length > 0 && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Invoice</div>
                {data.lineItems.map((l, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 0', borderTop: idx ? '1px solid var(--border)' : 'none' }}>
                    <span>{l.description}</span><span style={{ fontWeight: 600 }}>{fmtMoney(l.total)}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: '2px solid var(--navy)', fontWeight: 800 }}>
                  <span>Total</span><span>{fmtMoney(data.total)}</span>
                </div>
              </div>
            )}

            {data.kind === 'project' && (
              <div className="card" style={{ fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Invoiced</span><strong>{fmtMoney(data.total)}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}><span>Paid</span><strong>{fmtMoney(data.paid)}</strong></div>
              </div>
            )}
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <a href="/" style={{ color: 'var(--muted)', fontSize: 13 }}>{BRAND.name.full}</a>
        </div>
      </div>
    </div>
  );
}
